<script>
export default {
    name: 'ServicePlanSidebarCard',
};
</script>

<script setup>
import { computed, ref, watch } from 'vue';
import { capitalize } from 'lodash-es';
import { storeToRefs } from 'pinia';
import i18n from '@/plugins/i18n.ts';
import { useAppSidebarStore } from '@/store/pinia/app/sidebar.js';
import { FIELD } from '@/components/payments/components/service-plan/constants.js';
import ServicePlanFeatures from '@/components/payments/components/service-plan/components/features/components/index.vue';
import ServicePlanExpires from '@/components/payments/components/service-plan/components/expires.vue';
import useFeatures from '@/components/payments/components/service-plan/components/features/composables/use-features.js';

const props = defineProps({
    plan: {
        type: Object,
        required: true,
    },
    product: {
        type: [String, Array],
        required: true,
    },
    pricingRoute: {
        type: [String, Object],
    },
    featuresParams: {
        type: Object,
        default: () => ({}),
    },
});

const appSidebarStore = useAppSidebarStore();
const { collapsed } = storeToRefs(appSidebarStore);

const title = computed(() => i18n.t('payment.servicePlan.sidebarCard.title', { plan: props.plan[FIELD.LABEL] || `${capitalize(props.plan[FIELD.NAME])} Plan` }));
const features = computed(() => {
    const products = Array.isArray(props.product) ? props.product : [props.product];
    const options = props.plan?.[FIELD.OPTIONS] ?? {};

    const activeProduct = products.find(product => options[product]?.features);

    return activeProduct ? options[activeProduct].features : null;
});

const { visibleFeatures, hasExhaustion } = useFeatures(
    features,
    () => props.featuresParams,
);
const planWarning = hasExhaustion;

const opened = ref(false);
const collapseButtonText = computed(() => opened.value ? i18n.t('payment.servicePlan.sidebarCard.close') : i18n.t('payment.servicePlan.sidebarCard.open'));

watch(collapsed, () => {
    opened.value = false;
});
</script>

<template>
    <base-popover
        trigger="hover"
        placement="right"
        :disabled="!collapsed"
    >
        <template #reference>
            <div
                class="overflow-hidden rounded-md font-medium"
                :class="planWarning ? 'bg-amber-500' : 'bg-azure-600'"
            >
                <div
                    class="flex items-center space-x-3 px-3 py-2 text-white"
                    :class="{
                        'justify-center': collapsed,
                    }"
                >
                    <base-icon
                        name="cube"
                        prefix="fas"
                    />
                    <span v-if="!collapsed" class="text-sm" v-html="title" />
                </div>
                <div
                    v-if="features && !collapsed"
                    class="flex cursor-pointer items-center space-x-2 px-3 pb-2 text-sm select-none hover:text-white"
                    :class="planWarning ? 'text-amber-300' : 'text-azure-300'"
                    @click="opened = !opened"
                >
                    <base-icon
                        class="transition duration-300 ease-in-out"
                        :class="opened ? '-rotate-180' : 'rotate-0'"
                        name="chevron-down"
                        prefix="fas"
                        size="sm"
                    />
                    <span class="text-sm leading-none transition duration-300">
                        {{ collapseButtonText }}
                    </span>
                </div>

                <div
                    v-if="features"
                    class="overflow-y-clip transition-size duration-300 ease-in-out"
                    :class="[
                        planWarning ? 'bg-yellow-700' : 'bg-azure-700',
                        opened ? 'h-max' : 'h-0'
                    ]"
                >
                    <div class="grid gap-y-2 px-3 py-2">
                        <service-plan-features
                            class="gap-y-0.5 text-sm text-white"
                            :class="[planWarning ? 'marker:text-yellow-500' : 'marker:text-azure-300']"
                            :features="visibleFeatures"
                            :features-params="featuresParams"
                            as-list
                        >
                            <template v-if="plan[FIELD.EXPIRES_AT]" #suffix>
                                <div>
                                    <service-plan-expires
                                        class="text-white"
                                        :format="plan[FIELD.FORMAT]"
                                        :cycle-status="plan[FIELD.CYCLE_STATUS]"
                                        :expires-at="plan[FIELD.EXPIRES_AT]"
                                    />
                                </div>
                            </template>
                        </service-plan-features>

                        <div v-if="pricingRoute" class="flex flex-col text-sm text-white">
                            <span>
                                <b>
                                    {{ $t('payment.servicePlan.sidebarCard.needMore') }}
                                </b>
                            </span>
                            <router-link
                                class="text-white underline hover:text-gray-200 focus:text-gray-200 active:text-gray-200"
                                :to="pricingRoute"
                            >
                                {{ $t('payment.servicePlan.sidebarCard.viewMore') }}
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div
            v-if="features"
            class="overflow-y-clip transition-size duration-300 ease-in-out"
        >
            <div class="grid gap-y-2 px-3 py-2">
                <div class="flex flex-col gap-y-0.5">
                    <service-plan-features
                        class="gap-y-0.5"
                        :features="visibleFeatures"
                        :features-params="featuresParams"
                        as-list
                    >
                        <template v-if="plan[FIELD.EXPIRES_AT]" #suffix>
                            <div>
                                <service-plan-expires
                                    :format="plan[FIELD.FORMAT]"
                                    :cycle-status="plan[FIELD.CYCLE_STATUS]"
                                    :expires-at="plan[FIELD.EXPIRES_AT]"
                                />
                            </div>
                        </template>
                    </service-plan-features>
                </div>

                <div v-if="pricingRoute" class="flex flex-col text-sm">
                    <span>
                        <b>
                            {{ $t('payment.servicePlan.sidebarCard.needMore') }}
                        </b>
                    </span>
                    <router-link
                        class="underline"
                        :to="pricingRoute"
                    >
                        {{ $t('payment.servicePlan.sidebarCard.viewMore') }}
                    </router-link>
                </div>
            </div>
        </div>
    </base-popover>
</template>
